/**
 * Billing v2 — Formatting Utilities
 */

import { MICRO_USD_PER_USD } from '../constants'

/** Split a USD amount into its sign and absolute value, so the sign leads the `$`. */
function signAndAbs(usd: number): { negative: boolean; abs: number } {
  return { negative: usd < 0, abs: Math.abs(usd) }
}

/**
 * Format cents as USD string (e.g., 2900 → "$29.00")
 */
export function formatCentsAsUsd(cents: number): string {
  const { negative, abs } = signAndAbs(cents / 100)
  return `${negative ? '-' : ''}$${abs.toFixed(2)}`
}

/**
 * Headline price on customer-facing plan cards, without a period suffix.
 * A zero-price plan uses "$0" so its plan name can remain "Free" without
 * repeating "Free" in the price position.
 */
export function formatPlanPriceAmount(cents: number): string {
  return cents === 0 ? '$0' : formatCentsAsUsd(cents)
}

/** {@link formatPlanPriceAmount} with the monthly period appended. */
export function formatMonthlyPlanPrice(cents: number): string {
  const amount = formatPlanPriceAmount(cents)
  return cents === 0 ? amount : `${amount}/mo`
}

/**
 * Format microUSD as USD string (e.g., 5000000 → "$5.00", -1500000 → "-$1.50").
 *
 * The sign leads the currency symbol — a wallet in arrears must read "-$1.50",
 * not "$-1.50".
 */
export function formatMicroUsd(microUsd: number): string {
  const { negative, abs } = signAndAbs(microUsd / MICRO_USD_PER_USD)
  return `${negative ? '-' : ''}$${abs.toFixed(2)}`
}

/**
 * Format microUSD as USD with variable precision (e.g., 1500 → "$0.0015")
 */
export function formatMicroUsdPrecise(microUsd: number, decimals = 4): string {
  const { negative, abs } = signAndAbs(microUsd / MICRO_USD_PER_USD)
  return `${negative ? '-' : ''}$${abs.toFixed(decimals)}`
}

/**
 * Format microUSD as a signed USD string with explicit `+` for non-negatives
 * (e.g., 5000000 → "+$5.00", -3000000 → "-$3.00", 0 → "+$0.00").
 *
 * Use for ledger-style displays where sign carries meaning (admin wallet
 * adjustments, ledger transaction lists, signed margin / manual columns): the
 * leading `+` keeps the column unambiguous at a glance.
 */
export function formatMicroUsdSigned(microUsd: number): string {
  const { negative, abs } = signAndAbs(microUsd / MICRO_USD_PER_USD)
  return `${negative ? '-' : '+'}$${abs.toFixed(2)}`
}

/**
 * Parse a USD amount (as typed into a form) into microUSD.
 *
 * The inverse of {@link formatMicroUsd}, and the counterpart this module was
 * missing — every caller that needed dollars→µUSD was inlining
 * `value * 1_000_000` with its own rounding. Returns `null` for anything that
 * isn't a finite number, so a caller can distinguish "not a number" from `0`.
 */
export function parseUsdToMicroUsd(usd: string | number): number | null {
  if (typeof usd === 'string') {
    // `Number('')` is 0, not NaN — an empty input must not silently mean "$0.00".
    const trimmed = usd.trim()
    if (trimmed === '') return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? Math.round(parsed * MICRO_USD_PER_USD) : null
  }
  return Number.isFinite(usd) ? Math.round(usd * MICRO_USD_PER_USD) : null
}

/** A moment for messages sent without the reader's time zone, e.g. "Sep 26, 2027, 03:00 UTC". */
export function formatUtcDateTime(iso: string): string {
  const text = new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'UTC',
  })
  return `${text} UTC`
}
